/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState, ChangeEvent } from 'react';
import { Button, Paper, TextField } from '@material-ui/core';
import queryString from 'query-string';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import moment from 'moment';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';

import { DateTimeRange } from '../../../shared/components/DateTime/DateTimeRangePicker';
import { listSeries } from '../../../shared/services/listSeries';
import { getTimeseries } from '../../../shared/services/timeseries';
import { mainStyles } from '../../../shared/styles/common';
import CustomAndRelativeDateTimeRangePicker, {
  TimePickerButton,
} from '../../../shared/components/DateTime/CustomAndRelativeDateTimeRangePicker';
import MonitoringTimeseriesChart from './MonitoringTimeseriesChart';
import { colors } from '../../../shared/styles/colors';
import Loading from '../../../shared/components/Loading';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import SnackConfig from '../../../shared/config/snackConfig';

const useStyles = makeStyles((theme: Theme) => ({
  overview: {
    padding: '10px',
    marginBottom: '10px',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  margin: {
    margin: theme.spacing(1),
  },
  title: {
    textAlign: 'center',
  },
  spacing: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

const TimePickerButtons: TimePickerButton[] = [
  {
    variant: 'outlined',
    color: 'default',
    duration: moment.duration(1, 'hours'),
    display: '1H',
    hourlyTimeRange: 1,
  },
  {
    variant: 'outlined',
    color: 'default',
    duration: moment.duration(6, 'hours'),
    display: '6H',
    hourlyTimeRange: 6,
  },
  {
    variant: 'outlined',
    color: 'default',
    duration: moment.duration(1, 'days'),
    display: '1D',
    hourlyTimeRange: 24,
  },
];

const getTimePickerButtonByDisplay = (displayTime: string) =>
  TimePickerButtons.find((timePickerButton) => timePickerButton.display === displayTime);
const METRICS_FIRST_LOAD_LIMIT = 10;
type MonitoringProps = RouteComponentProps;

const Monitoring: React.FC<MonitoringProps> = ({ location, history }: MonitoringProps) => {
  const actualParams = queryString.parse(location.search) as Record<string, string>;
  let urlParams: Record<string, string>;
  const selectorSpaceFromParams = actualParams.selectorSpace || '';
  if ('relativeTime' in actualParams) {
    urlParams = { relativeTime: actualParams.relativeTime, selectorSpace: selectorSpaceFromParams };
    const matchingButton = getTimePickerButtonByDisplay(urlParams.relativeTime);
    // If passed relativeTime in the URL is not in the TimePickerButton default to 1H.
    if (typeof matchingButton === 'undefined') {
      urlParams.relativeTime = '1H';
    }
  } else if ('startTime' in actualParams && 'endTime' in actualParams) {
    urlParams = {
      startTime: actualParams.startTime,
      endTime: actualParams.endTime,
      selectorSpace: selectorSpaceFromParams,
    };
  } else {
    urlParams = { relativeTime: '1H', selectorSpace: selectorSpaceFromParams };
  }

  const isUrlParamsUpdated = () =>
    Object.keys(urlParams).length !== Object.keys(actualParams).length ||
    Object.entries(actualParams).some(([key, value]) => !(key in urlParams) || urlParams[key] !== value);

  if (isUrlParamsUpdated()) {
    history.replace({
      pathname: `${location.pathname}`,
      search: queryString.stringify(urlParams) as string,
    });
  }

  const [timeRange, setTimeRange] = useState<DateTimeRange>(() => {
    if ('relativeTime' in urlParams) {
      const matchingButton = getTimePickerButtonByDisplay(urlParams.relativeTime);
      return { start: moment().startOf('minute').subtract(matchingButton?.duration), end: moment().startOf('minute') };
    }
    return { start: moment(urlParams.startTime), end: moment(urlParams.endTime) };
  });
  const [isRelative, setIsRelative] = useState('relativeTime' in urlParams);

  const [submittedSelectorsSpace, setSubmittedSelectorsSpace] = useState<string>(urlParams.selectorSpace);
  const [inputSelectorsSpace, setInputSelectorsSpace] = useState<string>(urlParams.selectorSpace);
  const [selectorsInSubmittedSpace, setSelectorsInSubmittedSpace] = useState<Record<string, string>[]>([]);
  const [metricsData, setMetricsData] = useState<Record<string, (number | null)[][]>>({});
  const [running, setRunning] = useState<boolean>(false);
  const [limitMetricsInChart, setLimitMetricsInChart] = useState<boolean>(true);
  const [snackConfig, setSnackConfig] = useState(new SnackConfig({}));
  const [showChartPaper, setShowChartPaper] = useState(false);

  useEffect(() => {
    let shouldRerender = false;

    if (urlParams.selectorSpace !== submittedSelectorsSpace) {
      shouldRerender = true;
      setSubmittedSelectorsSpace(urlParams.selectorSpace);
    }

    let start = moment();
    let end = moment();
    let updateTimeRange = false;
    if ('startTime' in urlParams && 'endTime' in urlParams) {
      start = moment(urlParams.startTime);
      end = moment(urlParams.endTime);
      if (isRelative) {
        updateTimeRange = true;
      } else {
        const startTimeDiff = start.diff(timeRange.start);
        const endTimediff = end.diff(timeRange.end);
        updateTimeRange = startTimeDiff !== 0 || endTimediff !== 0;
      }
      setIsRelative(false);
    } else if ('relativeTime' in urlParams) {
      const matchingButton = getTimePickerButtonByDisplay(urlParams.relativeTime);
      start = moment().startOf('minute').subtract(matchingButton?.duration);
      end = moment().startOf('minute');
      if (!isRelative) {
        updateTimeRange = true;
      } else {
        const startTimeDiff = start.diff(timeRange.start);
        const endTimediff = end.diff(timeRange.end);
        updateTimeRange = startTimeDiff !== 0 || endTimediff !== 0;
      }
      setIsRelative(true);
    }

    if (updateTimeRange) {
      shouldRerender = true;
      setTimeRange({ start, end });
    }

    if (shouldRerender) {
      // On re-render, we should reset to render only top metrics
      setLimitMetricsInChart(true);
    }

    /**
     * Changing State based only on URL Parameters change when clicking browser back button.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.relativeTime, urlParams.startTime, urlParams.endTime, urlParams.selectorSpace]);

  useEffect(() => {
    let updatedHistorySearch: Record<string, string> | undefined;
    let timeRangeChange = false;

    if ('startTime' in urlParams && 'endTime' in urlParams) {
      if (isRelative) {
        timeRangeChange = true;
      } else {
        const startTimeDiff = moment(urlParams.startTime).diff(timeRange.start);
        const endTimeDiff = moment(urlParams.endTime).diff(timeRange.end);
        timeRangeChange = startTimeDiff !== 0 || endTimeDiff !== 0;
      }
    } else if ('relativeTime' in urlParams) {
      if (!isRelative) {
        timeRangeChange = true;
      } else {
        const relativeTimeMatchingButton = getTimePickerButtonByDisplay(urlParams.relativeTime);
        timeRangeChange =
          relativeTimeMatchingButton?.duration.asMilliseconds() !==
          moment.duration(timeRange.end.diff(timeRange.start)).asMilliseconds();
      }
    }

    if (timeRangeChange || submittedSelectorsSpace !== urlParams.selectorSpace) {
      delete urlParams.relativeTime;
      delete urlParams.startTime;
      delete urlParams.endTime;
      if (!isRelative) {
        urlParams.startTime = timeRange.start.toISOString();
        urlParams.endTime = timeRange.end.toISOString();
      } else {
        urlParams.relativeTime = TimePickerButtons.find(
          (timePickerButton) =>
            timePickerButton.duration.asMilliseconds() ===
            moment.duration(timeRange.end.diff(timeRange.start)).asMilliseconds()
        )!.display;
      }
      urlParams.selectorSpace = submittedSelectorsSpace;
      updatedHistorySearch = urlParams;
    }

    if (updatedHistorySearch) {
      setInputSelectorsSpace('');
      history.push({
        pathname: location.pathname,
        search: queryString.stringify(updatedHistorySearch) as string,
      });
      // On re-render, we should reset to render only top metrics
      setLimitMetricsInChart(true);
    }
    /**
     * Changing Url Parameters based only on state change triggered by an event.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedSelectorsSpace, timeRange, isRelative]);

  useEffect(() => {
    setShowChartPaper(submittedSelectorsSpace !== '');
    setRunning(true);
    setLimitMetricsInChart(true);
    setSelectorsInSubmittedSpace([]);
    setInputSelectorsSpace(submittedSelectorsSpace);
    if (submittedSelectorsSpace !== '') {
      void (async (): Promise<void> => {
        const listSeriesResponse = await listSeries(submittedSelectorsSpace);
        if (listSeriesResponse) {
          setSelectorsInSubmittedSpace(listSeriesResponse);
        } else {
          setRunning(false);
          setShowChartPaper(false);
          setSnackConfig(new SnackConfig({ open: true, message: 'There was an error getting all timeseries.' }));
        }
      })();
    } else {
      setRunning(false);
      setShowChartPaper(false);
    }
  }, [submittedSelectorsSpace]);

  useEffect(() => {
    setRunning(true);
    setInputSelectorsSpace(submittedSelectorsSpace);
    const metricsDataPromiseList: Promise<(number | null)[][] | null>[] = [];
    const indexToSelectorMapping: Record<number, string> = {};

    if (selectorsInSubmittedSpace.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      selectorsInSubmittedSpace.forEach((selector, idx) => {
        if ((limitMetricsInChart && idx + 1 <= METRICS_FIRST_LOAD_LIMIT) || !limitMetricsInChart) {
          indexToSelectorMapping[idx] = JSON.stringify(selector);
          const selectorClone = { ...selector };
          delete selectorClone.__name__;
          const metricsName = selector.__name__;
          metricsDataPromiseList.push(
            getTimeseries(timeRange.start.toISOString(), timeRange.end.toISOString(), metricsName, selectorClone)
          );
        }
      });
    }

    void (async (): Promise<void> => {
      const metricsDataList = await Promise.all(metricsDataPromiseList);
      const selectorMetricsData: Record<string, (number | null)[][]> = {};
      metricsDataList.forEach((metrics, idx) => {
        if (metrics) {
          selectorMetricsData[indexToSelectorMapping[idx]] = metrics;
        }
      });
      setMetricsData(selectorMetricsData);
      setRunning(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectorsInSubmittedSpace, timeRange, limitMetricsInChart]);

  const handleInputSelectorChange = (event: ChangeEvent<HTMLInputElement>) =>
    setInputSelectorsSpace(event.target.value);

  const classes = { ...mainStyles(), ...useStyles() };

  const submitQuery = () => {
    setSubmittedSelectorsSpace(inputSelectorsSpace);
  };

  return (
    <div className={classes.container}>
      <Paper className={classes.overview}>
        <h2 className={classes.title}>Anomaly Monitoring</h2>
        <CustomAndRelativeDateTimeRangePicker
          updateTimeRange={setTimeRange}
          setIsRelative={setIsRelative}
          isRelative={isRelative}
          initialTimeRange={timeRange}
          timePickerButtons={TimePickerButtons}
        />
        <div className={classes.spacing}>
          <TextField
            onChange={handleInputSelectorChange}
            value={inputSelectorsSpace}
            label="Query"
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />
        </div>
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={submitQuery}
          disabled={inputSelectorsSpace.length === 0 || inputSelectorsSpace === submittedSelectorsSpace}
          className={classes.margin}
        >
          Run query
        </Button>
        <br />
      </Paper>
      {showChartPaper && (
        <Paper className={classes.overview}>
          {running && <Loading />}
          {!running && limitMetricsInChart && selectorsInSubmittedSpace.length > METRICS_FIRST_LOAD_LIMIT && (
            <Alert severity="warning">
              Showing {METRICS_FIRST_LOAD_LIMIT} of {selectorsInSubmittedSpace.length} timeseries.
              <Button color="primary" onClick={() => setLimitMetricsInChart(false)}>
                Show all
              </Button>
            </Alert>
          )}
          {!running && Object.keys(metricsData).length !== 0 && (
            <MonitoringTimeseriesChart metricsData={metricsData} columnDefs={['time', 'value']} chartColors={colors} />
          )}
        </Paper>
      )}
      <SnackbarAlert snackConfig={snackConfig} setSnackConfig={setSnackConfig} />
    </div>
  );
};

export default withRouter(Monitoring);
